/**
 * 중앙화된 라우트 설정 파일
 * 
 * 이 파일에서 다음 항목들을 통합 관리합니다:
 * - 라우트 경로
 * - 사이드바 메뉴명
 * - Breadcrumb 이름
 * - 페이지 제목
 * - 페이지 설명
 * - 아이콘
 * - 권한
 * - API 호스트
 */

// import Squares2X2Icon from '@heroicons/react/24/outline/Squares2X2Icon';
import { BanknotesIcon } from '@heroicons/react/20/solid';

/**
 * 라우트 메타데이터 타입 정의
 * @typedef {Object} RouteMetadata
 * @property {string} path - 라우트 경로
 * @property {string} name - 메뉴/브레드크럼에 표시될 이름
 * @property {string} title - 페이지 제목 (PageHeader에 표시)
 * @property {string} [host] - API 호스트 URL (환경 변수에서 설정됨)
 * @property {string} [description] - 페이지 설명 (feature 컴포넌트에서 사용)
 * @property {React.ReactNode} [icon] - 사이드바 아이콘
 * @property {string[]} [roles] - 접근 가능한 역할 목록
 * @property {RouteMetadata[]} [submenu] - 하위 메뉴 (사이드바용)
 * @property {boolean} [hideInSidebar] - 사이드바에 표시하지 않음
 * @property {boolean} [hideInBreadcrumb] - Breadcrumb에 표시하지 않음
 * @property {Object} [detail] - 상세 페이지 설정
 * @property {string} [detail.title] - 상세 페이지 제목
 * @property {string} [detail.path] - 상세 페이지 경로 패턴
 * @property {Function} [detail.formatBreadcrumb] - Breadcrumb ID 포맷 함수
 */

const iconClasses = `h-6 w-6`;

/**
 * 전체 라우트 설정
 * 계층 구조로 정의하여 부모-자식 관계를 명확히 표현
 */
export const ROUTES_CONFIG = {
  // dashboard: {
  //   path: '/dashboard',
  //   name: 'Dashboard',
  //   title: 'Dashboard',
  //   description: '전체 시스템 현황을 한눈에 확인하세요',
  //   icon: <Squares2X2Icon className={iconClasses} />,
  //   roles: ['SUPER_ADMIN', 'EMPLOYEE'],
  // },
  
  payment: {
    path: '/payment',
    name: '결제 서비스',
    title: '결제 서비스',
    host: process.env.REACT_APP_PAYMENT_URL,
    icon: <BanknotesIcon className={`${iconClasses} inline`} />,
    roles: ['SUPER_ADMIN', 'EMPLOYEE'],
    hideInBreadcrumb: true, // payment는 실제 페이지가 아니므로 breadcrumb에서 제외
    
    children: {
      subscriptions: {
        path: '/payment/subscriptions',
        name: '구독',
        title: '구독 관리',
        description: '구독 정보를 조회하고 관리할 수 있습니다',
        roles: ['SUPER_ADMIN', 'EMPLOYEE'],
        detail: {
          path: '/payment/subscriptions/:id',
          title: '구독 상세',
          name: '구독 상세 정보',
          formTitle: '구독 정보', // DynamicForm 제목
          formatBreadcrumb: (id) => {
            return `구독 상세 정보 | ID : ${id}`;
          },
        },
      },
      
      subscriptionPlans: {
        path: '/payment/subscription-plans',
        name: '구독 계획',
        title: '구독 계획 관리',
        description: '구독 계획을 조회하고 관리할 수 있습니다',
        roles: ['SUPER_ADMIN', 'EMPLOYEE'],
        detail: {
          path: '/payment/subscription-plans/:id',
          title: '구독 계획 상세',
          name: '구독 계획 상세 정보',
          formTitle: '구독 계획 정보', // DynamicForm 제목
          formatBreadcrumb: (id) => {
            return `구독계획 상세 정보 | ID : ${id}`;
          },
        },
      },
      
      cards: {
        path: '/payment/cards',
        name: '카드',
        title: '카드 관리',
        description: '등록된 카드 정보를 조회하고 관리할 수 있습니다',
        roles: ['SUPER_ADMIN', 'EMPLOYEE'],
        detail: {
          path: '/payment/cards/:id',
          title: '카드 상세',
          name: '카드 상세 정보',
          formTitle: '카드 정보', // DynamicForm 제목
          formatBreadcrumb: (id) => {
            return `카드 상세 정보 | ID : ${id}`;
          },
        },
      },
      
      vbanks: {
        path: '/payment/vbanks',
        name: '가상계좌',
        title: '가상계좌 관리',
        description: '가상계좌 정보를 조회하고 관리할 수 있습니다',
        roles: ['SUPER_ADMIN', 'EMPLOYEE'],
        detail: {
          path: '/payment/vbanks/:id',
          title: '가상계좌 상세',
          name: '가상계좌 상세 정보',
          formTitle: '가상계좌 정보', // DynamicForm 제목
          formatBreadcrumb: (id) => {
            return `가상계좌 상세 정보 | ID : ${id}`;
          },
        },
      },
      
      transactions: {
        path: '/payment/transactions',
        name: '결제시도 내역',
        title: '결제시도 내역',
        description: '모든 결제 시도 내역을 조회할 수 있습니다',
        roles: ['SUPER_ADMIN', 'EMPLOYEE'],
        detail: {
          path: '/payment/transactions/:id',
          title: '결제시도 상세',
          name: '결제시도 상세 정보',
          formTitle: '결제시도 정보', // DynamicForm 제목
          formatBreadcrumb: (id) => {
            return `결제시도 상세 정보 | ID : ${id}`;
          },
        },
      },
    },
  },
  
  // 다른 메인 메뉴들도 동일한 패턴으로 추가 가능
  // basic: { ... },
  // billing: { ... },
  // etc.
};

/**
 * 플랫한 라우트 맵 생성 (경로 기반 빠른 조회용)
 * @returns {Object} path를 키로 하는 라우트 메타데이터 객체
 */
export const createFlatRouteMap = () => {
  const flatMap = {};
  
  const flatten = (config, parentPath = '', parentRoute = null) => {
    Object.values(config).forEach((route) => {
      if (parentRoute) {
        route.parent = parentRoute;
      }
      
      flatMap[route.path] = route;
      
      // 상세 페이지가 있는 경우
      if (route.detail?.path) {
        flatMap[route.detail.path] = {
          ...route.detail,
          parent: route, // 부모 라우트 참조 추가
        };
      }
      
      // 자식 라우트 재귀 처리
      if (route.children) {
        flatten(route.children, route.path, route);
      }
    });
  };
  
  flatten(ROUTES_CONFIG);
  return flatMap;
};

/**
 * 사이드바 메뉴 구조 생성
 * @returns {Array} 사이드바 메뉴 배열
 */
export const createSidebarRoutes = () => {
  const sidebarRoutes = [];
  
  Object.values(ROUTES_CONFIG).forEach((route) => {
    if (route.hideInSidebar) return;
    
    const menuItem = {
      path: route.path,
      name: route.name,
      icon: route.icon,
      roles: route.roles,
    };
    
    // submenu가 있는 경우
    if (route.children) {
      menuItem.path = ''; // 부모 메뉴는 클릭 불가
      menuItem.submenu = Object.values(route.children)
        .filter(child => !child.hideInSidebar)
        .map(child => ({
          path: child.path,
          name: child.name,
          roles: child.roles,
        }));
    }
    
    sidebarRoutes.push(menuItem);
  });
  
  return sidebarRoutes;
};

/**
 * React Router 라우트 배열 생성
 * @param {Function} lazyImport - lazy import 함수
 * @returns {Array} React Router routes 배열
 */
export const createReactRouterRoutes = (lazyImport) => {
  const routes = [];
  
  const addRoutes = (config) => {
    Object.entries(config).forEach(([key, route]) => {
      // 메인 라우트 추가
      if (!route.hideInSidebar || route.children) {
        routes.push({
          path: route.path,
          component: lazyImport(route.path),
          roles: route.roles,
        });
      }
      
      // 상세 페이지 라우트 추가
      if (route.detail?.path) {
        routes.push({
          path: route.detail.path,
          component: lazyImport(route.detail.path),
          roles: route.roles,
        });
      }
      
      // 자식 라우트 재귀 처리
      if (route.children) {
        addRoutes(route.children);
      }
    });
  };
  
  addRoutes(ROUTES_CONFIG);
  return routes;
};

/**
 * 경로를 기반으로 라우트 메타데이터 조회
 * @param {string} path - 조회할 경로
 * @returns {Object|null} 라우트 메타데이터 또는 null
 */
export const getRouteMetadata = (path) => {
  const flatMap = createFlatRouteMap();
  
  // 정확한 경로 매칭 시도
  if (flatMap[path]) {
    return flatMap[path];
  }
  
  // 동적 경로 매칭 시도 (/:id 패턴)
  const dynamicPath = path.replace(/\/[^/]+$/, '/:id');
  if (flatMap[dynamicPath]) {
    return flatMap[dynamicPath];
  }
  
  return null;
};

/**
 * 엔드포인트를 기반으로 API 호스트 URL 조회
 * @param {string} endpoint - API 엔드포인트 (예: '/api/v1/admin/subscriptions')
 * @returns {string} API 호스트 URL
 */
export const getApiHostFromEndpoint = (endpoint) => {
  // endpoint 패턴을 기반으로 호스트 결정
  const lowerEndpoint = endpoint.toLowerCase();
  
  // payment 서비스 관련 엔드포인트
  if (lowerEndpoint.includes('subscription') || 
      lowerEndpoint.includes('card') || 
      lowerEndpoint.includes('transaction') || 
      lowerEndpoint.includes('vbank')) {
    return process.env.REACT_APP_PAYMENT_URL || process.env.REACT_APP_BASE_URL || '';
  }
  
  // 기본값
  return process.env.REACT_APP_BASE_URL || '';
};

/**
 * 경로를 기반으로 API 호스트 URL 조회
 * 자식 라우트는 부모의 host를 상속받고, host 키값이 없으면 REACT_APP_BASE_URL을 기본값으로 사용
 * @param {string} path - 조회할 경로
 * @returns {string} API 호스트 URL
 */
export const getApiHost = (path) => {
  const metadata = getRouteMetadata(path);
  
  // 현재 라우트에 host가 있으면 반환
  if (metadata?.host) {
    return metadata.host;
  }
  
  // 부모 라우트의 host 확인 (자식이 부모의 host를 상속)
  if (metadata?.parent?.host) {
    return metadata.parent.host;
  }
  
  // 경로 세그먼트를 통해 부모 라우트 탐색
  const pathSegments = path.split('/').filter(Boolean);
  for (let i = pathSegments.length - 1; i >= 0; i--) {
    const parentPath = '/' + pathSegments.slice(0, i).join('/');
    const flatMap = createFlatRouteMap();
    const parentRoute = flatMap[parentPath];
    
    if (parentRoute?.host) {
      return parentRoute.host;
    }
  }
  
  // host 키값이 없으면 REACT_APP_BASE_URL을 기본값으로 사용
  return process.env.REACT_APP_BASE_URL || '';
};

/**
 * 첫 번째 라우트 경로 가져오기 (루트 리다이렉트용)
 * @returns {string} 첫 번째 라우트의 경로
 */
export const getFirstRoutePath = () => {
  const firstRouteKey = Object.keys(ROUTES_CONFIG)[0];
  const firstRoute = ROUTES_CONFIG[firstRouteKey];
  
  // 자식이 있으면 첫 번째 자식의 경로 반환
  if (firstRoute.children) {
    const firstChildKey = Object.keys(firstRoute.children)[0];
    return firstRoute.children[firstChildKey].path;
  }
  
  // 자식이 없으면 자신의 경로 반환
  return firstRoute.path;
};

/**
 * Breadcrumb 생성용 헬퍼 함수
 * @param {string} pathname - 현재 경로
 * @returns {Array} breadcrumb 배열
 */
export const generateBreadcrumbs = (pathname) => {
  const pathSegments = pathname.split('/').filter(Boolean);
  const breadcrumbs = [];
  const flatMap = createFlatRouteMap();
  
  let currentPath = '';
  
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // 동적 파라미터인 경우 (UUID, 숫자 등)
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
    const isNumeric = /^\d+$/.test(segment);
    
    if (isUuid || isNumeric) {
      // 상세 페이지 패턴으로 조회
      const detailPath = currentPath.replace(segment, ':id');
      const routeData = flatMap[detailPath];
      
      if (routeData) {
        // 커스텀 포맷 함수가 있으면 사용, 없으면 기본 포맷
        let displayName;
        if (routeData.formatBreadcrumb) {
          displayName = routeData.formatBreadcrumb(segment);
        } else {
          const displayId = isUuid ? segment.slice(0, 8) : segment;
          displayName = `${routeData.name} #${displayId}`;
        }
        
        breadcrumbs.push({
          name: displayName,
          path: currentPath,
          icon: routeData.icon,
          id: segment, // 원본 ID도 저장
        });
      }
    } else {
      const routeData = flatMap[currentPath];
      
      if (routeData && !routeData.hideInBreadcrumb) {
        breadcrumbs.push({
          name: routeData.name,
          path: routeData.path,
          icon: routeData.icon,
        });
      }
    }
  });
  
  return breadcrumbs;
};

/**
 * ====== 새 메뉴 추가 시 참고사항 ======
 * 
 * 1. ROUTES_CONFIG 객체에 새 메뉴를 추가할 때는 부모 레벨에만 host 키값을 설정합니다.
 *    (자식 라우트는 자동으로 부모의 host를 상속받습니다)
 * 
 * 2. 환경변수 설정:
 *    - .env, .env.dev, .env.prod 파일에 REACT_APP_<SERVICE>_URL을 추가
 *    - 예: REACT_APP_PAYMENT_URL, REACT_APP_CONTRACT_URL, REACT_APP_AUTH_URL 등
 *    - REACT_APP_BASE_URL: 기본 API 호스트 (host 미지정 시 사용)
 * 
 * 3. 메뉴 구조 예시:
 *    {
 *      path: '/contract',
 *      name: '계약 관리',
 *      title: '계약 관리',
 *      host: process.env.REACT_APP_CONTRACT_URL,  // 부모에만 설정
 *      icon: <DocumentIcon className={iconClasses} />,
 *      roles: ['SUPER_ADMIN', 'EMPLOYEE'],
 *      children: {
 *        contractList: {
 *          path: '/contract/list',
 *          name: '계약 목록',
 *          title: '계약 목록',
 *          // host 설정 불필요 - 부모의 host를 자동 상속
 *        },
 *      },
 *    }
 * 
 * 4. API 호스트 조회:
 *    - getApiHost(path) 함수를 사용하여 경로에 해당하는 API 호스트 URL을 조회
 *    - 자식 라우트는 부모의 host를 자동으로 상속받습니다
 *    - host 키값이 없으면 REACT_APP_BASE_URL을 기본값으로 사용
 *    
 *    예시:
 *    const paymentHost = getApiHost('/payment/subscriptions');  // REACT_APP_PAYMENT_URL 반환
 *    const cardHost = getApiHost('/payment/cards');            // REACT_APP_PAYMENT_URL 반환
 *    const defaultHost = getApiHost('/other/path');            // REACT_APP_BASE_URL 반환
 * 
 * 5. 라우트 메타데이터 조회:
 *    - getRouteMetadata(path) 함수를 사용하여 경로의 모든 정보 조회
 * 
 * 6. Axios 인스턴스에서 사용 예시:
 *    import { getApiHost } from './config/routes.config';
 *    
 *    const apiClient = axios.create();
 *    apiClient.interceptors.request.use((config) => {
 *      const currentPath = window.location.pathname;
 *      config.baseURL = getApiHost(currentPath);
 *      return config;
 *    });
 */

export default ROUTES_CONFIG;
